import React, {memo, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {AnyRouteResult} from '@/app/interfaces/routing';
import {usePageMetaSetter} from '@/common/services/headTagger';
import {useLocalizer} from '@/common/services/localizer';
import {useRoute} from '@/common/services/router';
import {selectDomainConfig} from '@/app/store/config/selectors';
import {addRecentLocationAction} from '@/app/store/locationsHistory/actions';
import {selectMapsData} from '@/app/store/maps/selectors';
import {useGetPageSeoMeta} from "@/app/hooks/useGetPageSeoMeta";
import {AdBanner} from '@/app/components/common/advertising';
import {WeatherMap} from '@/app/components/common/WeatherMap';
import {LocationBreadcrumbs} from '@/app/components/desktop/common/LocationBreadcrumbs';
import {PageHeader} from '@/app/components/desktop/common/PageHeader';
import {MapSettlementsList} from '../MapSettlementsList';
import {MapSubordinatesList} from '../MapSubordinatesList';
import styles from './MapPage.css';

export const MapPage = memo(() => {
	const {location, superior, dialect, map, relatesLocations} = useSelector(selectMapsData)!;

	const dispatch = useDispatch();
	const {route, urlGenerator} = useRoute<AnyRouteResult>();
	const {domainName} = useSelector(selectDomainConfig);

	const pageSEOMetadata = useGetPageSeoMeta();

	// Set SEO tags
	usePageMetaSetter({
		...pageSEOMetadata,
		canonicalUrl: `https://${domainName}${urlGenerator(route.name, route.params)}`.replace(/\/+$/, ''),
	});

	// Report about location view
	useEffect(() => {
		dispatch(addRecentLocationAction({location}));
	}, [location.id]);

	const t = useLocalizer();

	return (
		<>
			<PageHeader
				title={
					<h1>
						{t('weather')} <span className={styles.locationMame}>{location.titleLc}</span>
					</h1>
				}
				description={location.description}
				trailing={<AdBanner interfaceName='desktop' zoneName='header' />}
			/>

			<WeatherMap className={styles.mapView} params={map} />

			{relatesLocations.type === 'subordinates' ? (
				<MapSubordinatesList
					location={location}
					locationSuperiors={superior}
					dialect={dialect}
					largest={relatesLocations.largest}
					subordinates={relatesLocations.subordinates}
					continents={relatesLocations.continents}
				/>
			) : (
				<MapSettlementsList
					location={location}
					locationSuperiors={superior}
					settlements={relatesLocations.settlements}
				/>
			)}

			<LocationBreadcrumbs location={location} superiorLocations={superior} />
		</>
	);
});

MapPage.displayName = 'MapPage';
